import { useMemo } from "react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
  Rectangle,
} from "recharts"

// Custom solid bar shape for current period values
const SolidBarShape = (props: {
  x?: number
  y?: number
  width?: number
  height?: number
  fill?: string
}) => {
  const { x = 0, y = 0, width = 0, height = 0, fill } = props
  if (!height || height <= 0) return null

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      stroke={fill}
      strokeWidth={2}
      radius={[4, 4, 0, 0]}
    />
  )
}

// Custom dashed bar shape for comparison values (white dashed stroke + semi-transparent fill)
const DashedBarShape = (props: {
  x?: number
  y?: number
  width?: number
  height?: number
  fill?: string
}) => {
  const { x = 0, y = 0, width = 0, height = 0, fill } = props
  if (!height || height <= 0) return null

  return (
    <Rectangle
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      fillOpacity={0.7}
      stroke="#EB89B5"
      strokeWidth={2}
      strokeDasharray="5 2"
      radius={[4, 4, 0, 0]}
    />
  )
}
import ScrollableSection from "../ScrollableSection"
import { ChartPlaceholder } from "~/ui-rtk/components/atoms"
import { DateRange } from "../../../types"
import { type MarketingChartDataPoint } from "../../../hooks/useHomeDashboard"
import Card from "~/ui-rtk/components/ui/common/Card"
import { formatNumber } from "~/ui-rtk/utils/format"
import useDate from "~/ui-rtk/hooks/date"
import VerticalLegend from "~/ui-rtk/components/ui/charts/VerticalLegend/VerticalLegend"
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent"

const dayjs = useDate()

type MarketingTrendsSectionProps = {
  dateRange: DateRange
  chartData: MarketingChartDataPoint[]
  compareChartData?: MarketingChartDataPoint[]
  compareEnabled?: boolean
  isLoading?: boolean
  isShopifyConnected?: boolean
  isAmazonConnected?: boolean
  className?: string
}

// Legend rows for the spend/revenue chart. Each series gets a solid row and,
// when comparing, a dashed "(Prior)" row right after it. `short` is used only
// in the card title, which has to fit the header pill with four series.
type SpendRevenueSeries = {
  dataKey: string
  label: string
  color: string
  short?: string
  isDashed?: boolean
}

const SPEND_REVENUE_SERIES: SpendRevenueSeries[] = [
  { dataKey: "spend", label: "Spend", color: "#A57CFB" },
  { dataKey: "totalRevenue", label: "Total Revenue", color: "#66DECD" },
  {
    dataKey: "ncRevenue",
    label: "New Customer Revenue",
    short: "NC Revenue",
    color: "#F4D148",
  },
  { dataKey: "amazonRevenue", label: "Amazon Revenue", color: "#FF9900" },
]

const compareKey = (dataKey: string) =>
  `compare${dataKey[0].toUpperCase()}${dataKey.slice(1)}`

const spendRevenueGraphs = ({
  showNc,
  showAmazon,
  showCompare,
}: {
  showNc: boolean
  showAmazon: boolean
  showCompare: boolean
}) =>
  SPEND_REVENUE_SERIES.filter(
    g =>
      (g.dataKey !== "ncRevenue" || showNc) &&
      (g.dataKey !== "amazonRevenue" || showAmazon),
  ).flatMap<SpendRevenueSeries>(g =>
    showCompare
      ? [
          g,
          {
            dataKey: compareKey(g.dataKey),
            label: `${g.label} (Prior)`,
            color: g.color,
            isDashed: true,
          },
        ]
      : [g],
  )

const merGraphsWithAmer = [
  { dataKey: "mer", label: "MER", color: "#5C8BD6" },
  { dataKey: "aMer", label: "aMER", color: "#FFDD58" },
]

const merGraphsWithoutAmer = [
  { dataKey: "mer", label: "MER", color: "#5C8BD6" },
]

const merCompareGraphsWithAmer = [
  { dataKey: "mer", label: "MER", color: "#5C8BD6" },
  {
    dataKey: "compareMer",
    label: "MER (Prior)",
    color: "#5C8BD6",
    isDashed: true,
  },
  { dataKey: "aMer", label: "aMER", color: "#FFDD58" },
  {
    dataKey: "compareAMer",
    label: "aMER (Prior)",
    color: "#FFDD58",
    isDashed: true,
  },
]

const merCompareGraphsWithoutAmer = [
  { dataKey: "mer", label: "MER", color: "#5C8BD6" },
  {
    dataKey: "compareMer",
    label: "MER (Prior)",
    color: "#5C8BD6",
    isDashed: true,
  },
]

// Type for merged comparison data
type MergedChartDataPoint = MarketingChartDataPoint & {
  compareSpend?: number
  compareTotalRevenue?: number
  compareNcRevenue?: number
  compareAmazonRevenue?: number
  compareMer?: number
  compareAMer?: number
  compareDate?: string
}

// Color mapping for tooltip indicators
const SPEND_REVENUE_COLORS: Record<string, string> = {
  spend: "#A57CFB",
  totalRevenue: "#66DECD",
  ncRevenue: "#F4D148",
  amazonRevenue: "#FF9900",
  compareSpend: "#A57CFB",
  compareTotalRevenue: "#66DECD",
  compareNcRevenue: "#F4D148",
  compareAmazonRevenue: "#FF9900",
}

// Custom tooltip for spend/revenue chart
const SpendRevenueTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const data = payload[0].payload as MergedChartDataPoint
  const dateLabel = data.fullDate
    ? dayjs(data.fullDate).format("dddd, MMMM D, YYYY")
    : data.date
  const compareDateLabel = data.compareDate
    ? dayjs(data.compareDate).format("dddd, MMMM D, YYYY")
    : null

  // Group current and compare values
  const currentEntries = payload.filter(
    e => !String(e.dataKey).startsWith("compare"),
  )
  const compareEntries = payload.filter(e =>
    String(e.dataKey).startsWith("compare"),
  )

  return (
    <Card rounded className="bg-basic-black border border-basic-blue p-3">
      <p className="text-white/50 mb-2 text-sm">{dateLabel}</p>
      <div className="space-y-2">
        {currentEntries.map((entry, idx) => {
          const value = entry.value as number
          const color =
            SPEND_REVENUE_COLORS[entry.dataKey as string] || entry.color
          return (
            <div key={idx} className="flex items-center gap-2">
              <div
                className="w-1 h-6 rounded"
                style={{ backgroundColor: color }}
              />
              <div>
                <div className="text-white/60 text-xs">{entry.name}</div>
                <div className="text-white font-semibold">
                  {formatNumber(value, { currency: true, compact: true })}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {compareEntries.length > 0 && compareDateLabel && (
        <>
          <div className="border-t border-white/10 my-2" />
          <p className="text-white/40 mb-2 text-xs">
            Prior: {compareDateLabel}
          </p>
          <div className="space-y-2">
            {compareEntries.map((entry, idx) => {
              const value = entry.value as number
              const color =
                SPEND_REVENUE_COLORS[entry.dataKey as string] || entry.color
              return (
                <div key={idx} className="flex items-center gap-2">
                  <div
                    className="w-1 h-6 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <div>
                    <div className="text-white/60 text-xs">{entry.name}</div>
                    <div className="text-white font-semibold">
                      {formatNumber(value, { currency: true, compact: true })}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </Card>
  )
}

// Color mapping for MER tooltip indicators
const MER_COLORS: Record<string, string> = {
  mer: "#5C8BD6",
  aMer: "#FFDD58",
  compareMer: "#5C8BD6",
  compareAMer: "#FFDD58",
}

// Custom tooltip for MER/aMER chart
const MerTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const data = payload[0].payload as MergedChartDataPoint
  const dateLabel = data.fullDate
    ? dayjs(data.fullDate).format("dddd, MMMM D, YYYY")
    : data.date
  const compareDateLabel = data.compareDate
    ? dayjs(data.compareDate).format("dddd, MMMM D, YYYY")
    : null

  // Group current and compare values
  const currentEntries = payload.filter(
    e => !String(e.dataKey).startsWith("compare"),
  )
  const compareEntries = payload.filter(e =>
    String(e.dataKey).startsWith("compare"),
  )

  return (
    <Card rounded className="bg-basic-black border border-basic-blue p-3">
      <p className="text-white/50 mb-2 text-sm">{dateLabel}</p>
      <div className="space-y-2">
        {currentEntries.map((entry, idx) => {
          const color = MER_COLORS[entry.dataKey as string] || entry.color
          return (
            <div key={idx} className="flex items-center gap-2">
              <div
                className="w-1 h-6 rounded"
                style={{ backgroundColor: color }}
              />
              <div>
                <div className="text-white/60 text-xs">{entry.name}</div>
                <div className="text-white font-semibold">
                  {(entry.value as number).toFixed(2)}x
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {compareEntries.length > 0 && compareDateLabel && (
        <>
          <div className="border-t border-white/10 my-2" />
          <p className="text-white/40 mb-2 text-xs">
            Prior: {compareDateLabel}
          </p>
          <div className="space-y-2">
            {compareEntries.map((entry, idx) => {
              const color = MER_COLORS[entry.dataKey as string] || entry.color
              return (
                <div key={idx} className="flex items-center gap-2">
                  <div
                    className="w-1 h-6 rounded"
                    style={{ backgroundColor: color }}
                  />
                  <div>
                    <div className="text-white/60 text-xs">{entry.name}</div>
                    <div className="text-white font-semibold">
                      {(entry.value as number).toFixed(2)}x
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </Card>
  )
}

const SpendRevenueChart = ({
  data,
  compareData,
  compareEnabled,
  isLoading,
  isShopifyConnected = true,
  isAmazonConnected = false,
}: {
  data: MarketingChartDataPoint[]
  compareData?: MarketingChartDataPoint[]
  compareEnabled?: boolean
  isLoading?: boolean
  isShopifyConnected?: boolean
  isAmazonConnected?: boolean
}) => {
  // Merge data with comparison data by index (matching by position in date range)
  const mergedData = useMemo<MergedChartDataPoint[]>(() => {
    if (!compareEnabled || !compareData?.length) return data

    return data.map((item, index) => {
      const compareItem = compareData[index]
      return {
        ...item,
        compareSpend: compareItem?.spend,
        compareTotalRevenue: compareItem?.totalRevenue,
        compareNcRevenue: compareItem?.ncRevenue,
        compareAmazonRevenue: compareItem?.amazonRevenue,
        compareDate: compareItem?.fullDate,
      }
    })
  }, [data, compareData, compareEnabled])

  if (isLoading) {
    return <ChartPlaceholder title="Loading Spend vs Revenue Chart..." />
  }

  if (!data.length) {
    return (
      <ChartPlaceholder
        title="No data available for this date range"
        icon="📊"
      />
    )
  }

  const showCompare = compareEnabled && compareData && compareData.length > 0

  return (
    <div className="flex flex-col">
      <div className="h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={mergedData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#303367"
              vertical={false}
            />
            {/* Primary X axis for current period bars */}
            <XAxis
              dataKey="date"
              xAxisId={0}
              stroke="#667085"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            {/* Hidden X axis for comparison bars (same position) */}
            <XAxis dataKey="date" xAxisId={1} hide />
            <YAxis
              stroke="#667085"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
            />
            <Tooltip
              content={<SpendRevenueTooltip />}
              cursor={{ fill: "rgba(165, 124, 251, 0.1)" }}
            />
            {/* Current period bars (solid, rendered first) */}

            <Bar
              dataKey="spend"
              name="Spend"
              xAxisId={0}
              barSize={24}
              shape={<SolidBarShape fill="#A57CFB" />}
            />
            <Bar
              dataKey="totalRevenue"
              name="Total Revenue"
              xAxisId={0}
              barSize={24}
              shape={<SolidBarShape fill="#66DECD" />}
            />
            {isShopifyConnected && (
              <Bar
                dataKey="ncRevenue"
                name="New Customer Revenue"
                xAxisId={0}
                barSize={24}
                shape={<SolidBarShape fill="#F4D148" />}
              />
            )}
            {isAmazonConnected && (
              <Bar
                dataKey="amazonRevenue"
                name="Amazon Revenue"
                xAxisId={0}
                barSize={24}
                shape={<SolidBarShape fill="#FF9900" />}
              />
            )}
            {showCompare && (
              <>
                <Bar
                  dataKey="compareSpend"
                  name="Spend (Prior)"
                  xAxisId={1}
                  barSize={24}
                  shape={<DashedBarShape fill="#A57CFB" />}
                />
                <Bar
                  dataKey="compareTotalRevenue"
                  name="Total Revenue (Prior)"
                  xAxisId={1}
                  barSize={24}
                  shape={<DashedBarShape fill="#66DECD" />}
                />
                {isShopifyConnected && (
                  <Bar
                    dataKey="compareNcRevenue"
                    name="New Customer Revenue (Prior)"
                    xAxisId={1}
                    barSize={24}
                    shape={<DashedBarShape fill="#F4D148" />}
                  />
                )}
                {isAmazonConnected && (
                  <Bar
                    dataKey="compareAmazonRevenue"
                    name="Amazon Revenue (Prior)"
                    xAxisId={1}
                    barSize={24}
                    shape={<DashedBarShape fill="#FF9900" />}
                  />
                )}
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4">
        <VerticalLegend
          graphs={spendRevenueGraphs({
            showNc: isShopifyConnected,
            showAmazon: isAmazonConnected,
            showCompare: !!showCompare,
          })}
        />
      </div>
    </div>
  )
}

const MerAmerChart = ({
  data,
  compareData,
  compareEnabled,
  isLoading,
  isShopifyConnected = true,
}: {
  data: MarketingChartDataPoint[]
  compareData?: MarketingChartDataPoint[]
  compareEnabled?: boolean
  isLoading?: boolean
  isShopifyConnected?: boolean
}) => {
  // Merge data with comparison data by index (matching by position in date range)
  const mergedData = useMemo<MergedChartDataPoint[]>(() => {
    if (!compareEnabled || !compareData?.length) return data

    return data.map((item, index) => {
      const compareItem = compareData[index]
      return {
        ...item,
        compareMer: compareItem?.mer,
        compareAMer: compareItem?.aMer,
        compareDate: compareItem?.fullDate,
      }
    })
  }, [data, compareData, compareEnabled])

  if (isLoading) {
    return <ChartPlaceholder title="Loading MER vs aMER Chart..." />
  }

  if (!data.length) {
    return (
      <ChartPlaceholder
        title="No data available for this date range"
        icon="📊"
      />
    )
  }

  const showCompare = compareEnabled && compareData && compareData.length > 0

  return (
    <div className="flex flex-col">
      <div className="h-[320px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={mergedData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            barGap={8}
            barCategoryGap="15%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#303367"
              vertical={false}
            />
            {/* Primary X axis for current period bars */}
            <XAxis
              dataKey="date"
              xAxisId={0}
              stroke="#667085"
              fontSize={12}
              tickLine={false}
              axisLine={false}
            />
            {/* Hidden X axis for comparison bars (same position) */}
            <XAxis dataKey="date" xAxisId={1} hide />
            <YAxis
              stroke="#667085"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              domain={[0, "auto"]}
            />
            <Tooltip
              content={<MerTooltip />}
              cursor={{ fill: "rgba(92, 139, 214, 0.1)" }}
            />
            {/* Comparison bars (dashed, rendered first/behind) */}

            {/* Current period bars (solid, rendered on top) */}
            <Bar
              dataKey="mer"
              name="MER"
              xAxisId={0}
              barSize={30}
              shape={<SolidBarShape fill="#5C8BD6" />}
            />
            {isShopifyConnected && (
              <Bar
                dataKey="aMer"
                name="aMER"
                xAxisId={0}
                barSize={30}
                shape={<SolidBarShape fill="#FFDD58" />}
              />
            )}

            {showCompare && (
              <>
                <Bar
                  dataKey="compareMer"
                  name="MER (Prior)"
                  xAxisId={1}
                  barSize={30}
                  shape={<DashedBarShape fill="#5C8BD6" />}
                />
                {isShopifyConnected && (
                  <Bar
                    dataKey="compareAMer"
                    name="aMER (Prior)"
                    xAxisId={1}
                    barSize={30}
                    shape={<DashedBarShape fill="#FFDD58" />}
                  />
                )}
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4">
        <VerticalLegend
          graphs={(() => {
            if (showCompare) {
              return isShopifyConnected
                ? merCompareGraphsWithAmer
                : merCompareGraphsWithoutAmer
            }
            return isShopifyConnected ? merGraphsWithAmer : merGraphsWithoutAmer
          })()}
        />
      </div>
    </div>
  )
}

export default function MarketingTrendsSection({
  dateRange: _dateRange,
  chartData,
  compareChartData = [],
  compareEnabled = false,
  isLoading = false,
  isShopifyConnected = true,
  isAmazonConnected = false,
  className,
}: MarketingTrendsSectionProps) {
  // Suppress unused variable warning - dateRange is passed for consistency
  void _dateRange

  const chartItems = useMemo(
    () => [
      {
        id: "spendVsRevenue",
        title: spendRevenueGraphs({
          showNc: isShopifyConnected,
          showAmazon: isAmazonConnected,
          showCompare: false,
        })
          .map(g => g.short ?? g.label)
          .join(" vs "),
        content: (
          <SpendRevenueChart
            data={chartData}
            compareData={compareChartData}
            compareEnabled={compareEnabled}
            isLoading={isLoading}
            isShopifyConnected={isShopifyConnected}
            isAmazonConnected={isAmazonConnected}
          />
        ),
      },
      {
        id: "merVsAmer",
        title: isShopifyConnected ? "MER vs aMER" : "MER",
        content: (
          <MerAmerChart
            data={chartData}
            compareData={compareChartData}
            compareEnabled={compareEnabled}
            isLoading={isLoading}
            isShopifyConnected={isShopifyConnected}
          />
        ),
      },
    ],
    [
      chartData,
      compareChartData,
      compareEnabled,
      isLoading,
      isShopifyConnected,
      isAmazonConnected,
    ],
  )

  return (
    <ScrollableSection
      title="Marketing Trends"
      items={chartItems}
      className={className}
    />
  )
}
